/** @format */
import React, { useEffect, useState } from 'react';
import Card from '../../../components/Card/Card';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import CardBody from '../../../components/Card/CardBody';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import DiscountTable from '../Components/DiscountTable';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import DiscountForm from '../Components/DiscountForm';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { Moment } from 'moment';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { DiscountHelper } from '../../../helpers/DiscountHelper';
import { useParams } from 'react-router-dom';
import { configDefault } from '../../../core/config';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import i18n from '../../../core/i18n';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { makeStyles } from '@material-ui/core';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { useDispatch } from 'react-redux';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { DISCOUNT_CONSTANTS } from '../../../modules/discounts/reducers';
import { DiscountCollectionInterface } from '../../../interfaces/api/DiscountCollectionInterface';
import { DiscountInterface } from '../../../interfaces/billing/DiscountInterface';
import { FilterReducerFieldName } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import AlertConfirm from '../../../components/Info/AlertConfirm';

interface PlatformResellerDiscountInterface {
    platformResellerId?: string | number | null;
}

export interface DiscountEntity {
    name: string | null;
    active: boolean;
    onlyThisPromo: boolean;
    type: number;
    count: number | null;
    historyLength: string | number | null;
    dateStart: Moment | string | null;
    dateEnd: Moment | string | null;
    gracePeriod: Moment | string | null;
    payment: boolean;
    skipInitialPrice: boolean;
    firstMonthOnly: boolean;
    propOnFirstMonth: boolean | null;
    filterType: number;
    platformResellerId?: number | string;
    destination: string | null;
    autoShare: boolean;
    oneTime: boolean;
    rateParam: string | null;
    params: string | null;
    allowUseOnProduct: number;
    customPrefix: string | null;
    useAllResellerNumbers: boolean;
    priority: number;
}

export interface DiscountParamEntity {
    discountId: number;
    count: number;
    destination: Array<number | string>;
    bond: number;
    historyLength: number;
    oneTime: boolean;
    periods: string;
    flags: number;
    linearBillingOverride: number | null;
}

const PlatformResellerDiscount = (props: PlatformResellerDiscountInterface): React.ReactElement => {
    const useStyle = makeStyles(adescomStyle);
    const dispatch = useDispatch();
    const { id } = useParams();
    const idAsNumber = parseInt(id.split('/')[id.split('/').length - 1]);
    const useReducer = false;
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [discounts, setDiscounts] = useState<DiscountCollectionInterface | null>(null);
    const [activeDiscount, setActiveDiscount] = useState<DiscountInterface | null>(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

    const URI = '/discounts';

    const initialEntity = {
        customerId: -1,
        name: null,
        active: false,
        onlyThisPromo: false,
        type: DiscountHelper.TYPES.TYPE_FREE_MINUTES_GLOBAL,
        count: null,
        historyLength: '0',
        dateStart: DateHelperTs.getCurrentDateTime(),
        dateEnd: null,
        gracePeriod: null,
        payment: false,
        skipInitialPrice: false,
        firstMonthOnly: false,
        propOnFirstMonth: null,
        filterType: DiscountHelper.DESTINATION.DESTINATION_ANYTHING,
        autoShare: false,
        platformResellerId: idAsNumber,
        oneTime: false,
        destination: null,
        rateParam: null,
        params: null,
        allowUseOnProduct: -1,
        customPrefix: null,
        useAllResellerNumbers: false,
        priority: 1,
        discountParam:''
    };

    const [discountAddFormOpen, setDiscountAddFormOpen] = useState<boolean>(false);

    const [payments, setPayments] = useState<Array<{ amount: number; date: Moment | string }>>([]);
    const [entity, setEntity] = useState<DiscountEntity>(initialEntity);

    const [entityStatus, setEntityStatus] = useState<any>({
        name: null,
        active: null,
        onlyThisPromo: null,
        type: null,
        value: null,
        historyLength: null,
        dateStart: null,
        dateEnd: null,
        gracePeriod: null,
        payment: null,
        skipInitialPrice: null,
        firstMonthOnly: null,
        propOnFirstMonth: null,
        filterType: null,
        autoShare: null,
        oneTime: null,
        allowUseOnProduct: null,
        customPrefix: null,
        priority: null,
    });

    const updateOrders = (orders: Array<Order>): void => setOrders(orders);
    const updateFilters = (filters: Array<Filter>): void => setFilters(filters);

    const openAddDiscountModal = (): void => {
        setEntity(initialEntity);
        setDiscountAddFormOpen(true);
    };

    const closeAddDiscountModal = (): void => {
        setEntity(initialEntity);
        setDiscountAddFormOpen(false);
        setActiveDiscount(null);
    };

    const search = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
            DISCOUNT_CONSTANTS.LIST,
            [...filters, { name: 'rateDiscount', value: false }, { name: 'platformResellerId', value: idAsNumber }],
            orders,
            (data: DiscountCollectionInterface | null) => {
                if (!useReducer) {
                    setDiscounts(data);
                }
            },
            { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
        );
    };

    const clearFilters = (): void => {
        setFilters([]);
        setOrders([]);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const editDiscount = (el: DiscountInterface): void => {
        setActiveDiscount(el);
        setEntity(DiscountHelper.setEntityValueFromDB(el, idAsNumber));
        setDiscountAddFormOpen(true);
    };
    const activateDiscount = (el: DiscountInterface): void => {
        setActiveDiscount(el);
        setEntity({ ...DiscountHelper.setEntityValueFromDB(el, idAsNumber), active: true });
        setConfirmDialogOpen(true);
    };

    const cancelActivation = (): void => {
        setActiveDiscount(null);
        setEntity(initialEntity);
        setConfirmDialogOpen(false);
    };

    const saveDiscount = (): void => {
        const errors = DiscountHelper.validateDiscountEntityBeforePersist(entity);
        if (errors.length > 0) {
            errors.map((el: { key: string; value: string }) => {
                NotificationHelper.notifyError(el.value, dispatch);
            });

            return;
        }

        if (confirmDialogOpen || discountAddFormOpen) {
            setConfirmDialogOpen(false);
            setDiscountAddFormOpen(false);
            setActiveDiscount(null);
            setEntity(initialEntity);
        }

        const itemLoaders = new ItemLoaders(dispatch);
        const loadData = (forceLoad = false): void => {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(URI, configDefault.PLATFORM_API_PREFIX)),
                DISCOUNT_CONSTANTS.ADD,
                DiscountHelper.prepareFinalDiscountWithParams(entity),
                {
                    onSuccess: (data: DiscountInterface) => {
                        cancelActivation();
                        if (idAsNumber) {
                            const itemLoader = new ItemLoaders(dispatch);
                            itemLoader.getListWithCallback(
                                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
                                DISCOUNT_CONSTANTS.LIST,
                                [
                                    { name: 'rateDiscount', value: false },
                                    { name: 'platformResellerId', value: idAsNumber },
                                ],
                                [],
                                (data: DiscountCollectionInterface | null) => {
                                    if (!useReducer || forceLoad) {
                                        setDiscounts(data);
                                    }
                                },
                                { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
                            );
                        }
                        NotificationHelper.notifySuccess('discount:n.success_add', dispatch);
                    },
                    onError: (error: any) => {
                        NotificationHelper.notifyError(i18n.t(error.message.message), dispatch);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: true,
                }
            );
        }
        if (activeDiscount) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${URI}/${activeDiscount.id}`, configDefault.PLATFORM_API_PREFIX)),
                DISCOUNT_CONSTANTS.EDIT,
                DiscountHelper.prepareFinalDiscountWithParams(entity),
                {
                    onSuccess: (data: DiscountInterface) => {
                        cancelActivation();
                        if (idAsNumber) {
                            const itemLoader = new ItemLoaders(dispatch);
                            itemLoader.getListWithCallback(
                                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
                                DISCOUNT_CONSTANTS.LIST,
                                [
                                    { name: 'rateDiscount', value: false },
                                    { name: 'platformResellerId', value: idAsNumber },
                                ],
                                [],
                                (data: DiscountCollectionInterface | null) => {
                                    if (!useReducer) {
                                        setDiscounts(data);
                                    }
                                },
                                { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
                            );
                        }

                        NotificationHelper.notifySuccess('discount:n.success_edit', dispatch);
                    },
                    onError: (error: any) => {
                        NotificationHelper.notifyError(i18n.t(error.message.message), dispatch);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: true,
                }
            );
        } else {
            loadData(true);
    };
    }

    const paginationCallback = (uri?: string): void => {
        if (!idAsNumber || !uri) {
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            DISCOUNT_CONSTANTS.LIST,
            [
                { name: 'rateDiscount', value: false },
                { name: 'platformResellerId', value: idAsNumber },
            ],
            [],
            (data: DiscountCollectionInterface | null) => {
                if (!useReducer) {
                    setDiscounts(data);
                }
            },
            { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
        );
    };

    useEffect(() => {
        if (!idAsNumber) {
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
            DISCOUNT_CONSTANTS.LIST,
            [
                { name: 'rateDiscount', value: false },
                { name: 'platformResellerId', value: idAsNumber },
            ],
            [],
            (data: DiscountCollectionInterface | null) => {
                if (!useReducer) {
                    setDiscounts(data);
                }
            },
            { propagateDataToCallback: !useReducer, dispatchAfterCallback: useReducer }
        );
    }, [dispatch, useReducer, idAsNumber]);
    //
    // useEffect(() => {
    //     console.log(entity);
    // }, [entity]);

    return (
        <Card>
            <CardHeader icon={<LocalOfferIcon />} trans={'discount:h.discount'} />
            <CardBody>
                <DiscountTable
                    openAddDiscountModal={openAddDiscountModal}
                    discounts={discounts}
                    filters={filters}
                    orders={orders}
                    updateOrders={updateOrders}
                    updateFilters={updateFilters}
                    search={search}
                    clearFilters={clearFilters}
                    editDiscount={editDiscount}
                    activateDiscount={activateDiscount}
                    paginationCallback={paginationCallback}
                />
                <GeneralDialog
                    dialogType={'action'}
                    agreeCallback={saveDiscount}
                    agreeCallbackTextLabel={'general:common.save'}
                    disagreeCallbackTextLabel={'general:common:cancel'}
                    disagreeCallback={closeAddDiscountModal}
                    open={discountAddFormOpen}
                    maxWidth={'lg'}
                    title={'discount:h.add_discount_form'}
                >
                    <GridContainer>
                        <GridItem xs={12}>
                            <DiscountForm
                                entity={entity}
                                setEntity={setEntity}
                                entityStatus={entityStatus}
                                setEntityStatus={setEntityStatus}
                                payments={payments}
                                setPayments={setPayments}
                            />
                        </GridItem>
                    </GridContainer>
                </GeneralDialog>
                {confirmDialogOpen && (
                    <AlertConfirm
                        content={'Potwierdź aktywację'}
                        confirmBtnText={'Potwierdź'}
                        cancelBtnText={'Anuluj'}
                        onConfirm={saveDiscount}
                        onCancel={cancelActivation}
                    />
                )}
            </CardBody>
        </Card>
    );
};

export default PlatformResellerDiscount;
